import { UserNew } from 'grommet-icons'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Box, Collapsible, RadioButtonGroup, Text, TextInput } from 'grommet'

import { useAuth } from 'hooks/useAuth'
import { useProvider } from 'hooks/useMoodPlot'
import FormSection from 'components/form/FormSection'
import ImageFromStorage from 'components/storage/ImageFromStorage'

import PlotBox from './PlotBox'
import { AvatarWrapper } from '../moodsList/AvatarWrapper'
import { AnonymousAvatar } from '../moodsList/AnonymousAvatar'
import { trackEvent } from 'utils/useGTM'

type PlotSelection = 'my-name' | 'display-name' | 'anonymous'

type Props = { allowAnonymous?: boolean }
export default function DisplayNameBox({ allowAnonymous = true }: Props) {
  const { t } = useTranslation()

  const { user } = useAuth()
  const fullName = user?.user_metadata?.full_name
  const avatarUrl = user?.user_metadata?.avatar_url
  const {
    state: { anon, displayName },
    setAnonymous,
  } = useProvider()

  const initialSelection = useMemo<PlotSelection>(() => {
    if (displayName) return 'display-name'
    if (anon && allowAnonymous) return 'anonymous'
    if (fullName) return 'my-name'
    return 'display-name'
  }, [allowAnonymous, anon, displayName, fullName])

  const initialLocalAnonName = useMemo(() => {
    return displayName ?? ''
  }, [displayName])

  const [selected, setSelected] = useState<PlotSelection>(initialSelection)
  const [localAnonName, setLocalAnonName] = useState(initialLocalAnonName)

  useEffect(() => {
    switch (selected) {
      case 'my-name':
        if (anon) {
          setAnonymous(false)
          trackEvent({ event: 'PlotAsSelectUser' })
        }
        break
      case 'display-name':
        if (localAnonName !== displayName) {
          setAnonymous(true, localAnonName ?? null)
          // Trigger this event only once on first radio button click
          if (localAnonName === '') {
            trackEvent({ event: 'PlotAsSelectDisplayName' })
          }
        }
        break
      case 'anonymous':
        if (!anon || displayName) {
          setAnonymous(true)
          trackEvent({ event: 'PlotAsSelectAnonymous' })
        }
        break
    }
  }, [anon, displayName, localAnonName, selected, setAnonymous])

  const displayNameOpt = useMemo(
    () => ({
      value: 'display-name',
      label: (
        <OptionLabel
          label={t('components.anonymous-picker.type-display-name')}
          icon={
            // We can also show person's avatar when using display-name (TO BE DEFINED)
            // <ImageFromStorage size="22px" url={account?.avatar_url} />
            <AvatarWrapper background="brand" size="22px">
              <AnonymousAvatar size="12px" name={localAnonName.trim()} />
            </AvatarWrapper>
          }
        />
      ),
    }),
    [localAnonName, t],
  )
  const anonymousOpt = useMemo(
    () => ({
      value: 'anonymous',
      label: (
        <OptionLabel
          label={t('components.anonymous-picker.type-anonymous')}
          icon={
            <Avatar background="brand" size="22px">
              <UserNew size="12px" />
            </Avatar>
          }
        />
      ),
    }),
    [t],
  )

  const options = useMemo(() => {
    // Add anonymous option based on the group config
    const defaultOptions = allowAnonymous ? [anonymousOpt, displayNameOpt] : [displayNameOpt]

    // Add the account name if user is logged in
    if (fullName) {
      const myNameOpt = {
        value: 'my-name',
        label: (
          <OptionLabel
            label={fullName ?? ''}
            icon={<ImageFromStorage size="22px" iconSize="16px" url={avatarUrl} />}
          />
        ),
      }
      return [myNameOpt, ...defaultOptions]
    }
    return defaultOptions
  }, [fullName, avatarUrl, allowAnonymous, anonymousOpt, displayNameOpt])

  return (
    <PlotBox elevation="small">
      <FormSection gap="small" margin={{ vertical: 'small' }}>
        <Box margin={{ horizontal: 'small' }}>
          <Text size="xsmall">{t('components.anonymous-picker.description')}</Text>
        </Box>
        {options.length > 1 && (
          <RadioButtonGroup
            pad={{ left: 'small' }}
            name="name"
            options={options}
            value={selected}
            onChange={(event) => setSelected(event.target.value as PlotSelection)}
          />
        )}
        <Collapsible open={selected === 'display-name'}>
          <TextInput
            name="name"
            size="small"
            maxLength={30}
            value={localAnonName}
            autoComplete="first-name"
            placeholder={
              <Text size="xsmall">
                {t('components.anonymous-picker.type-display-name-placeholder')}
              </Text>
            }
            onChange={(e) => setLocalAnonName(e.target.value.trimLeft())}
          />
        </Collapsible>
      </FormSection>
    </PlotBox>
  )
}

const OptionLabel = ({ label, icon }: { label: string; icon: React.ReactNode }) => (
  <Box direction="row" justify="between" align="center" gap="small">
    <Box>{icon}</Box>
    <Box>
      <Text size="small">{label}</Text>
    </Box>
  </Box>
)
